// @generated-imports
import { escapeHTML, recomposeGames, showToast, truncate, updateContinueButton } from './app.js';
import { idbDatabaseExists } from './backup.js';
import { CONFIG, dom, state } from './core.js';
import { LOCAL_AMBIGUOUS_EXTENSIONS, LOCAL_CORE_LABELS, LOCAL_EXTENSION_CORE_MAP } from './dragdrop.js';
import { formatBytes } from './format.js';
import { layerClosed, layerOpened } from './layers.js';
import { LOCAL_SYSTEM_LABEL } from './platform.js';
import { renderRecentGames } from './prefs.js';
import { startSession } from './session.js';
import { latestStateMeta, startGameWithResume } from './states.js';
import { analyzeZipRom, getLocalGameTitle, openCorePicker } from './zipdetect.js';
// @generated-imports
/* ===========================================================================
   BIBLIOTECA PESSOAL (v2.0)
   ---------------------------------------------------------------------------
   Duas stores separadas: METADADOS em `emutab-library` (leves — o catálogo
   desenha cards sem tocar em arquivo) e ARQUIVOS em `emutab-local-roms`
   (File/Blob, lidos só ao jogar). Identidade por hash SHA-256 do conteúdo
   + core (arquivos iguais renomeados não duplicam; iguais em nome e tamanho
   mas diferentes não colidem). Importação em lote com fila e erro por
   item; edição de nome/core/capa; remoção em lote; export/import de
   metadados; BIOS por core; migração automática dos ids `local:slug-…`
   da 1.6.x preservando recentes, Continuar, contadores e tempo.
   ======================================================================== */

const LIBRARY_DB = 'emutab-library';
const LIBRARY_META_STORE = 'meta';
const LIBRARY_BIOS_STORE = 'bios';
const LOCAL_ROMS_STORE = 'roms';

function isLocalRomId(id) {
  return typeof id === 'string' && (id.indexOf('local:') === 0 || id.indexOf('lib:') === 0);
}

/* --- Bancos -------------------------------------------------------------- */

function openLibraryDb() {
  return new Promise((resolve, reject) => {
    let request;
    try { request = indexedDB.open(LIBRARY_DB, 1); } catch (error) { reject(error); return; }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(LIBRARY_META_STORE)) db.createObjectStore(LIBRARY_META_STORE, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(LIBRARY_BIOS_STORE)) db.createObjectStore(LIBRARY_BIOS_STORE, { keyPath: 'core' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function openLocalRomsDb() {
  return new Promise((resolve, reject) => {
    let request;
    try { request = indexedDB.open(CONFIG.localRomsDbName, 1); } catch (error) { reject(error); return; }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(LOCAL_ROMS_STORE)) db.createObjectStore(LOCAL_ROMS_STORE, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function withStore(openDb, storeName, mode, work) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    let result;
    try { result = work(store); } catch (error) { db.close(); reject(error); return; }
    tx.oncomplete = () => { db.close(); resolve(result); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error || new Error('abort')); };
  }));
}

function requestValue(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function readAll(openDb, storeName) {
  const db = await openDb();
  try {
    return (await requestValue(db.transaction(storeName, 'readonly').objectStore(storeName).getAll())) || [];
  } finally { db.close(); }
}

/* --- Hash de conteúdo ---------------------------------------------------- */

async function hashFile(file) {
  const buffer = await file.arrayBuffer();
  if (crypto && crypto.subtle && typeof crypto.subtle.digest === 'function') {
    const digest = await crypto.subtle.digest('SHA-256', buffer);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  // Fallback (contextos sem WebCrypto): FNV-1a de 32 bits + tamanho.
  const bytes = new Uint8Array(buffer);
  let hash = 0x811c9dc5;
  for (let i = 0; i < bytes.length; i += 1) { hash ^= bytes[i]; hash = (hash * 0x01000193) >>> 0; }
  return `fnv-${hash.toString(16)}-${bytes.length}`;
}

function libraryId(hash, core) {
  return `lib:${hash.slice(0, 16)}:${core}`;
}

/* --- Carga e catálogo ---------------------------------------------------- */

/** Carrega os METADADOS (leve) e migra entradas antigas se houver. */
async function loadLocalRomLibrary() {
  state.localRoms = new Map();
  if (!window.indexedDB) return;
  try {
    if (await idbDatabaseExists(LIBRARY_DB)) {
      const metas = await readAll(openLibraryDb, LIBRARY_META_STORE);
      metas.forEach((meta) => state.localRoms.set(meta.id, meta));
    }
  } catch (_) { /* biblioteca vazia */ }
  try {
    await migrateLegacyLibrary();
  } catch (_) { /* migração é melhor esforço; entradas antigas continuam jogáveis */ }
}

/** 1.6.x guardava {id:'local:slug-tamanho', file, …} tudo junto. Agora o
 *  arquivo fica em `roms` e o metadado em `meta`, com id por hash. */
async function migrateLegacyLibrary() {
  if (!(await idbDatabaseExists(CONFIG.localRomsDbName))) return;
  const records = await readAll(openLocalRomsDb, LOCAL_ROMS_STORE);
  const legacy = records.filter((r) => r && r.id && r.id.indexOf('local:') === 0 && r.file);
  if (!legacy.length) return;
  const mapping = {};
  for (const old of legacy) {
    // eslint-disable-next-line no-await-in-loop
    const hash = await hashFile(old.file);
    const id = libraryId(hash, old.core);
    const meta = {
      id, hash, title: old.title, core: old.core,
      system: old.system || LOCAL_CORE_LABELS[old.core] || old.core,
      fileName: old.fileName || old.file.name, size: old.file.size,
      addedAt: old.addedAt || Date.now(), cover: null, persisted: true,
    };
    // eslint-disable-next-line no-await-in-loop
    await withStore(openLocalRomsDb, LOCAL_ROMS_STORE, 'readwrite', (store) => { store.put({ id, file: old.file }); store.delete(old.id); });
    // eslint-disable-next-line no-await-in-loop
    await withStore(openLibraryDb, LIBRARY_META_STORE, 'readwrite', (store) => { store.put(meta); });
    state.localRoms.set(id, meta);
    mapping[old.id] = id;
  }
  remapLocalIds(mapping);
}

/** Atualiza recentes, Continuar, contadores e tempo para os ids novos. */
function remapLocalIds(mapping) {
  const map = (id) => mapping[id] || id;
  state.recentGames = [...new Set(state.recentGames.map(map))];
  try { localStorage.setItem(CONFIG.localStorageRecentKey, JSON.stringify(state.recentGames)); } catch (_) {}
  const last = localStorage.getItem(CONFIG.localStorageLastGameKey);
  if (last && mapping[last]) { try { localStorage.setItem(CONFIG.localStorageLastGameKey, mapping[last]); } catch (_) {} }
  const remapObject = (obj) => {
    const out = {};
    Object.keys(obj || {}).forEach((key) => { out[map(key)] = (out[map(key)] || 0) + (Number(obj[key]) || 0); });
    return out;
  };
  state.playCounts = remapObject(state.playCounts);
  state.playTime = remapObject(state.playTime);
  try { localStorage.setItem(CONFIG.localStoragePlayCountsKey, JSON.stringify(state.playCounts)); } catch (_) {}
  try { localStorage.setItem(CONFIG.localStoragePlayTimeKey, JSON.stringify(state.playTime)); } catch (_) {}
}

function localRomAsGame(meta) {
  return {
    id: meta.id,
    title: meta.title,
    description: `ROM do seu dispositivo (${meta.fileName || 'arquivo local'}${meta.size ? `, ${formatBytes(meta.size)}` : ''}). Fica guardada neste navegador.`,
    year: meta.year ? String(meta.year) : (meta.addedAt ? String(new Date(meta.addedAt).getFullYear()) : '—'),
    genre: meta.genre || 'Local',
    system: LOCAL_SYSTEM_LABEL,
    core: meta.core,
    featured: false,
    // v2.1: editáveis — entram nos filtros de jogadores e na tela P1/P2.
    players: Math.min(4, Math.max(1, Number(meta.players) || 1)),
    coop: Array.isArray(meta.modes) && meta.modes.includes('coop'),
    modes: Array.isArray(meta.modes) ? meta.modes.slice() : [],
    region: '',
    tags: meta.persisted === false ? ['local', 'só nesta sessão'] : ['local'],
    added: meta.addedAt ? new Date(meta.addedAt).toISOString().slice(0, 10) : '',
    // Sem capa escolhida: a miniatura do último estado vira a capa.
    cover: meta.cover || ((latestStateMeta(meta.id) || {}).thumbnail) || '',
    screenshot: '',
    romUrl: '',
    fileName: meta.fileName || '',
    isLocal: true,
  };
}

async function getLocalRomFile(id) {
  const meta = state.localRoms.get(id);
  if (meta && meta.sessionFile) return meta.sessionFile; // não persistida: só nesta sessão
  const db = await openLocalRomsDb();
  try {
    const record = await requestValue(db.transaction(LOCAL_ROMS_STORE, 'readonly').objectStore(LOCAL_ROMS_STORE).get(id));
    return record ? record.file : null;
  } finally { db.close(); }
}

/* --- Importação (arquivo único ou lote) ---------------------------------- */

/** Importa um arquivo: hash → dedupe → grava arquivo + metadado. */
async function importLocalRomFile(file, core, options = {}) {
  const title = options.title || getLocalGameTitle(file.name);
  const hash = await hashFile(file);
  const id = libraryId(hash, core);
  if (state.localRoms.has(id) && !options.replace) {
    return { status: 'duplicate', id, title: state.localRoms.get(id).title };
  }
  const meta = {
    id, hash, title, core,
    system: LOCAL_CORE_LABELS[core] || core,
    fileName: file.name, size: file.size, addedAt: Date.now(), cover: null, persisted: true,
  };
  try {
    await withStore(openLocalRomsDb, LOCAL_ROMS_STORE, 'readwrite', (store) => { store.put({ id, file }); });
    await withStore(openLibraryDb, LIBRARY_META_STORE, 'readwrite', (store) => { store.put(meta); });
  } catch (error) {
    meta.persisted = false;
    meta.sessionFile = file; // jogável nesta sessão; card avisa
    state.localRoms.set(id, meta);
    return { status: 'session-only', id, title, error };
  }
  state.localRoms.set(id, meta);
  return { status: 'ok', id, title };
}

/** Fila visível: itens, status e erro individual. */
async function runImportQueue(items) {
  if (!items.length) return [];
  if (dom.importQueueModal) {
    dom.importQueueList.innerHTML = items.map((item, index) => `<div class="backup-row data-row" data-queue-index="${index}">
      <span class="data-info">${escapeHTML(truncate(item.file.name, 40))} <small>${formatBytes(item.file.size)} • ${escapeHTML(LOCAL_CORE_LABELS[item.core] || item.core || 'core?')}</small></span>
      <span class="queue-status">aguardando</span>
    </div>`).join('');
    dom.importQueueSummary.textContent = `0 de ${items.length}`;
    dom.importQueueModal.classList.remove('is-hidden');
    layerOpened('import-queue', dom.importQueueClose);
  }
  const results = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const row = dom.importQueueList ? dom.importQueueList.querySelector(`[data-queue-index="${i}"] .queue-status`) : null;
    if (row) row.textContent = 'importando…';
    let result;
    try {
      if (!item.core) throw new Error('sistema não identificado');
      // eslint-disable-next-line no-await-in-loop
      result = await importLocalRomFile(item.file, item.core);
    } catch (error) {
      result = { status: 'error', error, title: item.file.name };
    }
    results.push(result);
    if (row) {
      row.textContent = result.status === 'ok' ? '✅ guardada'
        : result.status === 'duplicate' ? `↔ já existia (${result.title})`
          : result.status === 'session-only' ? '⚠️ só nesta sessão'
            : `❌ ${(result.error && result.error.message) || 'erro'}`;
      row.className = `queue-status is-${result.status}`;
    }
    if (dom.importQueueSummary) dom.importQueueSummary.textContent = `${i + 1} de ${items.length}`;
  }
  recomposeGames();
  renderLocalRomsList();
  const ok = results.filter((r) => r.status === 'ok').length;
  const dup = results.filter((r) => r.status === 'duplicate').length;
  const bad = results.filter((r) => r.status === 'error' || r.status === 'session-only').length;
  showToast(`Importação: ${ok} guardada(s), ${dup} repetida(s), ${bad} com problema.`);
  return results;
}

function closeImportQueue() {
  if (!dom.importQueueModal) return;
  dom.importQueueModal.classList.add('is-hidden');
  layerClosed('import-queue');
}

/** Coleta arquivos de um DataTransfer (inclui pastas via webkitGetAsEntry). */
async function collectDroppedFiles(dataTransfer) {
  const files = [];
  const items = dataTransfer && dataTransfer.items ? [...dataTransfer.items] : [];
  const entries = items.map((item) => (typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null));
  if (entries.some(Boolean)) {
    for (const entry of entries) {
      // eslint-disable-next-line no-await-in-loop
      if (entry) await walkEntry(entry, files);
    }
    return files;
  }
  return dataTransfer && dataTransfer.files ? [...dataTransfer.files] : [];
}

function walkEntry(entry, out) {
  return new Promise((resolve) => {
    if (entry.isFile) {
      entry.file((file) => { out.push(file); resolve(); }, () => resolve());
      return;
    }
    if (entry.isDirectory) {
      const reader = entry.createReader();
      const readBatch = () => reader.readEntries(async (batch) => {
        if (!batch.length) { resolve(); return; }
        for (const child of batch) {
          // eslint-disable-next-line no-await-in-loop
          await walkEntry(child, out);
        }
        readBatch();
      }, () => resolve());
      readBatch();
      return;
    }
    resolve();
  });
}

/** Lote: detecta o core de cada arquivo; quando ambíguo, pergunta UMA vez
 *  por extensão (não jogo a jogo). */
async function importFilesBatch(files) {
  const romFiles = files.filter((file) => isSupportedRomFile(file));
  if (!romFiles.length) { showToast('Nenhum arquivo de ROM reconhecido.'); return; }
  const items = [];
  const askedByExt = new Map();
  for (const file of romFiles) {
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    let core = LOCAL_EXTENSION_CORE_MAP[ext] || null;
    let candidates = null;
    if (!core && ext === 'zip') {
      // eslint-disable-next-line no-await-in-loop
      const analysis = await analyzeZipRom(file).catch(() => null);
      if (analysis && analysis.core) core = analysis.core;
      else if (analysis && analysis.candidates && analysis.candidates.length) candidates = analysis.candidates;
    }
    if (!core) {
      // Pergunta UMA vez por extensão ambígua (não jogo a jogo).
      if (!askedByExt.has(ext)) {
        // eslint-disable-next-line no-await-in-loop
        const choice = await openCorePicker(file.name, ext, candidates || LOCAL_AMBIGUOUS_EXTENSIONS[ext] || Object.keys(LOCAL_CORE_LABELS), null);
        askedByExt.set(ext, choice || null);
      }
      core = askedByExt.get(ext);
    }
    items.push({ file, core });
  }
  await runImportQueue(items);
}

function isSupportedRomFile(file) {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return Boolean(LOCAL_EXTENSION_CORE_MAP[ext] || LOCAL_AMBIGUOUS_EXTENSIONS[ext]);
}

/* --- Edição, remoção e metadados ----------------------------------------- */

async function updateLocalRomMeta(id, changes) {
  const meta = state.localRoms.get(id);
  if (!meta) return false;
  const next = Object.assign({}, meta, changes);
  delete next.sessionFile;
  try {
    await withStore(openLibraryDb, LIBRARY_META_STORE, 'readwrite', (store) => { store.put(next); });
  } catch (_) { return false; }
  state.localRoms.set(id, Object.assign(meta, changes));
  recomposeGames();
  renderLocalRomsList();
  return true;
}

async function removeLocalRomEntry(id) {
  const entry = state.localRoms.get(id);
  state.localRoms.delete(id);
  try {
    await withStore(openLocalRomsDb, LOCAL_ROMS_STORE, 'readwrite', (store) => { store.delete(id); });
    await withStore(openLibraryDb, LIBRARY_META_STORE, 'readwrite', (store) => { store.delete(id); });
  } catch (_) { /* a remoção em memória já vale para esta sessão */ }
  state.recentGames = state.recentGames.filter((gameId) => gameId !== id);
  try { localStorage.setItem(CONFIG.localStorageRecentKey, JSON.stringify(state.recentGames)); } catch (_) {}
  if (localStorage.getItem(CONFIG.localStorageLastGameKey) === id) localStorage.removeItem(CONFIG.localStorageLastGameKey);
  recomposeGames();
  renderRecentGames();
  updateContinueButton();
  renderLocalRomsList();
  showToast(entry ? `"${truncate(entry.title, 30)}" removida da biblioteca.` : 'ROM local removida.');
}

async function removeLocalRomsBatch(ids) {
  for (const id of ids) {
    // eslint-disable-next-line no-await-in-loop
    await removeLocalRomEntry(id);
  }
}

function exportLibraryMetadata() {
  const metas = [...state.localRoms.values()].map((meta) => ({
    id: meta.id, hash: meta.hash, title: meta.title, core: meta.core, system: meta.system,
    fileName: meta.fileName, size: meta.size, addedAt: meta.addedAt, cover: meta.cover || null,
    genre: meta.genre || '', year: meta.year || '', players: meta.players || 1, modes: meta.modes || [],
  }));
  const blob = new Blob([JSON.stringify({ format: 'emutab-library', version: 1, items: metas }, null, 2)], { type: 'application/json' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `emutab-biblioteca-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(link.href), 5000);
}

async function importLibraryMetadata(file) {
  let payload;
  try { payload = JSON.parse(await file.text()); } catch (_) { showToast('Arquivo de metadados inválido.'); return; }
  if (!payload || payload.format !== 'emutab-library' || !Array.isArray(payload.items)) { showToast('Este arquivo não é uma exportação de biblioteca do EmuTab.'); return; }
  let applied = 0;
  for (const item of payload.items) {
    const meta = state.localRoms.get(item.id);
    if (!meta) continue; // só ajusta metadados de arquivos que EXISTEM aqui
    // eslint-disable-next-line no-await-in-loop
    const changes = { title: item.title || meta.title, core: item.core || meta.core, system: item.system || meta.system, cover: item.cover || meta.cover };
    if (item.genre) changes.genre = item.genre;
    if (item.year) changes.year = item.year;
    if (item.players) changes.players = item.players;
    if (Array.isArray(item.modes)) changes.modes = item.modes;
    // eslint-disable-next-line no-await-in-loop
    if (await updateLocalRomMeta(item.id, changes)) applied += 1;
  }
  showToast(`${applied} metadado(s) aplicados (arquivos que ainda não estão aqui foram ignorados).`);
}

/* --- BIOS por core -------------------------------------------------------- */

async function saveBiosFile(core, file) {
  try {
    await withStore(openLibraryDb, LIBRARY_BIOS_STORE, 'readwrite', (store) => { store.put({ core, file, fileName: file.name, savedAt: Date.now() }); });
    return true;
  } catch (_) { return false; }
}

async function getBiosFile(core) {
  try {
    if (!(await idbDatabaseExists(LIBRARY_DB))) return null;
    const db = await openLibraryDb();
    try {
      const record = await requestValue(db.transaction(LIBRARY_BIOS_STORE, 'readonly').objectStore(LIBRARY_BIOS_STORE).get(core));
      return record ? record.file : null;
    } finally { db.close(); }
  } catch (_) { return null; }
}

/* --- Painel: lista com seleção e edição --------------------------------- */

function renderLocalRomsList() {
  if (!dom.localRomsList) return;
  const entries = [...state.localRoms.values()].sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));
  if (!entries.length) {
    dom.localRomsList.innerHTML = '<div class="backup-row">Nenhuma ROM local guardada ainda — solte arquivos (ou uma pasta) na página, ou use Abrir ROM.</div>';
    return;
  }
  dom.localRomsList.innerHTML = entries.map((entry) => `<div class="backup-row data-row local-rom-row">
      <input type="checkbox" class="local-rom-check" data-select-local="${escapeHTML(entry.id)}" aria-label="Selecionar ${escapeHTML(entry.title)}">
      <span class="data-info" title="${escapeHTML(entry.fileName || '')}">${escapeHTML(truncate(entry.title, 30))}
        <small>${escapeHTML(entry.system || entry.core)} • ${formatBytes(entry.size || 0)}${entry.persisted === false ? ' • só nesta sessão' : ''}</small></span>
      <span class="data-actions">
        <button class="mini-btn" type="button" data-edit-local="${escapeHTML(entry.id)}">Editar</button>
        <button class="mini-btn mini-btn-danger" type="button" data-remove-local="${escapeHTML(entry.id)}">Remover</button>
      </span>
    </div>`).join('')
    + `<div class="backup-row data-row">
      <span class="data-info"><small>${entries.length} ROM(s) • selecione para remover em lote</small></span>
      <span class="data-actions">
        <button class="mini-btn" type="button" data-library-export="1">Exportar metadados</button>
        <button class="mini-btn" type="button" data-library-import="1">Importar metadados</button>
        <button class="mini-btn mini-btn-danger" type="button" data-remove-selected="1">Remover selecionadas</button>
      </span>
    </div>`;
}

function openLibraryEdit(id) {
  const meta = state.localRoms.get(id);
  if (!meta || !dom.libraryEditModal) return;
  dom.libraryEditTitle.value = meta.title;
  dom.libraryEditCore.innerHTML = Object.keys(LOCAL_CORE_LABELS).map((core) => `<option value="${core}"${core === meta.core ? ' selected' : ''}>${escapeHTML(LOCAL_CORE_LABELS[core])}</option>`).join('');
  dom.libraryEditModal.dataset.editId = id;
  dom.libraryEditCover.value = '';
  if (dom.libraryEditGenre) dom.libraryEditGenre.value = meta.genre || '';
  if (dom.libraryEditYear) dom.libraryEditYear.value = meta.year || '';
  if (dom.libraryEditPlayers) dom.libraryEditPlayers.value = String(meta.players || 1);
  if (dom.libraryEditModes) {
    const modes = Array.isArray(meta.modes) ? meta.modes : [];
    dom.libraryEditModes.querySelectorAll('input[type="checkbox"]').forEach((box) => { box.checked = modes.includes(box.value); });
  }
  dom.libraryEditModal.classList.remove('is-hidden');
  layerOpened('library-edit', dom.libraryEditTitle);
}

function closeLibraryEdit() {
  if (!dom.libraryEditModal) return;
  dom.libraryEditModal.classList.add('is-hidden');
  layerClosed('library-edit');
}

async function submitLibraryEdit() {
  const id = dom.libraryEditModal.dataset.editId;
  const meta = state.localRoms.get(id);
  if (!meta) { closeLibraryEdit(); return; }
  const changes = {
    title: dom.libraryEditTitle.value.trim() || meta.title,
    core: dom.libraryEditCore.value || meta.core,
  };
  changes.system = LOCAL_CORE_LABELS[changes.core] || changes.core;
  if (dom.libraryEditGenre) changes.genre = dom.libraryEditGenre.value.trim().slice(0, 30);
  if (dom.libraryEditYear) {
    const year = Number(dom.libraryEditYear.value);
    changes.year = year >= 1970 && year <= 2100 ? String(year) : '';
  }
  if (dom.libraryEditPlayers) changes.players = Math.min(4, Math.max(1, Number(dom.libraryEditPlayers.value) || 1));
  if (dom.libraryEditModes) {
    changes.modes = changes.players > 1
      ? [...dom.libraryEditModes.querySelectorAll('input[type="checkbox"]')].filter((box) => box.checked).map((box) => box.value)
      : [];
  }
  const coverFile = dom.libraryEditCover.files && dom.libraryEditCover.files[0];
  if (coverFile) {
    changes.cover = await fileToDataUrl(coverFile, 360);
  }
  const ok = await updateLocalRomMeta(id, changes);
  showToast(ok ? 'ROM atualizada.' : 'Não deu para salvar as alterações.');
  closeLibraryEdit();
}

function fileToDataUrl(file, maxWidth) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxWidth / Math.max(1, img.width));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.8));
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

/* --- Compat com chamadas antigas ---------------------------------------- */

async function continueLocalRom(id) {
  const meta = state.localRoms.get(id);
  if (!meta) { showToast('A ROM local salva não está mais disponível neste navegador.'); return; }
  const game = localRomAsGame(meta);
  await startGameWithResume(game);
}

/** Um único arquivo (drag & drop / Abrir ROM): importa e já joga. */
async function playLocalRom(file, core, options = {}) {
  const result = await importLocalRomFile(file, core, { title: options.title });
  if (result.status === 'error') {
    showToast(`Não deu para importar "${file.name}": ${(result.error && result.error.message) || 'erro'}`);
    return;
  }
  recomposeGames();
  const meta = state.localRoms.get(result.id);
  await startSession({ kind: 'local', gameId: result.id, core: meta.core, title: meta.title, file, options });
}

// @generated-exports
export { LIBRARY_BIOS_STORE, LIBRARY_DB, LIBRARY_META_STORE, LOCAL_ROMS_STORE, closeImportQueue, closeLibraryEdit, collectDroppedFiles, continueLocalRom, exportLibraryMetadata, fileToDataUrl, getBiosFile, getLocalRomFile, hashFile, importFilesBatch, importLibraryMetadata, importLocalRomFile, isLocalRomId, isSupportedRomFile, libraryId, loadLocalRomLibrary, localRomAsGame, migrateLegacyLibrary, openLibraryDb, openLibraryEdit, openLocalRomsDb, playLocalRom, readAll, remapLocalIds, removeLocalRomEntry, removeLocalRomsBatch, renderLocalRomsList, requestValue, runImportQueue, saveBiosFile, submitLibraryEdit, updateLocalRomMeta, walkEntry, withStore };
// @generated-exports
